import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI. Receives the caught error. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary.
 *
 * Catches unhandled render errors anywhere in the tree and displays a
 * recovery UI instead of a blank screen. When the observability module is
 * enabled (VITE_MODULE_OBSERVABILITY + VITE_SENTRY_DSN), caught errors are
 * forwarded to Sentry via `captureObservabilityException`. This is a no-op
 * when the module is disabled — no @sentry/react import cost is paid.
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always log to console so local dev has visibility.
    console.error('[AppErrorBoundary] Uncaught render error:', error, info);

    // Forward to Sentry when the observability module is enabled.
    // captureObservabilityException is a no-op when disabled — no SDK import cost.
    import('../../shared/observability')
      .then(({ captureObservabilityException }) => {
        captureObservabilityException(error, { componentStack: info.componentStack ?? '' });
      })
      .catch(() => {
        // Never let observability wiring break error boundary logic.
      });
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error !== null) {
      if (fallback) {
        return fallback(error, this.handleReset);
      }

      // Minimal default fallback — design-system thread will style this.
      return (
        <div role="alert" style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
          <h1>Something went wrong</h1>
          <p>{error.message}</p>
          <button onClick={this.handleReset} type="button">
            Try again
          </button>
        </div>
      );
    }

    return children;
  }
}
